// ╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝�?// Task Review Page (Employer)
// ╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝╝�?
import { useState, useEffect, useCallback, useMemo, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/ToastProvider';
import {
  NotSubmittedStatusBadge,
  StatusBadge,
  TaskInstanceInventoryPhotoImg,
  TaskInstancePhotoImg,
  OsmEmbedInteractiveMap,
} from '../../components/task';
import { ReceiptImageLightbox } from '../../components/receipt/ReceiptImageLightbox';
import { CommentThread } from '../../components/comments';
import { ApiClientError, inventoryApi, taskInstanceApi } from '../../lib/api';
import { emitTaskKindNotAllowed, isLimitReachedError } from '../../lib/limitError';
import { navigateBack } from '../../lib/navigateBack';
import { portalToBody } from '../../lib/portalToBody';
import { EmployerFloatingReviewBar } from '../../components/employer/EmployerFloatingReviewBar';
import { AppButton } from '../../components/ui/AppButton';
import { AppModal } from '../../components/ui/AppModal';
import { AppPageTitleSection } from '../../components/ui/AppPageTitleSection';
import { PreviousHelperBanner } from '../../components/pairing/PreviousHelperBanner';
import {
  FIXED_ABOVE_BOTTOM_NAV,
  SCROLL_BOTTOM_FOR_REVIEW_AND_FAB,
  SCROLL_BOTTOM_FOR_REVIEW_BAR,
} from '../../lib/layoutChrome';
import { PagePrimaryFab } from '../../components/ui/PagePrimaryFab';
import { buildInventoryV2CategoryBadgeIndexMap } from '../../lib/inventoryCategoryBadge';
import { InventoryCategoryBadge } from '../../components/inventory/InventoryCategoryBadge';
import { InventorySummaryStatCardChevron } from '../../components/inventory/InventorySummaryStatCardChevron';
import { InventorySummaryStatDetailModal } from '../../components/inventory/InventorySummaryStatDetailModal';
import { HomeStockLevelIcon } from '../../components/inventory/HomeStockLevelIcon';
import { HomeStockLevelLegend } from '../../components/inventory/HomeStockLevelLegend';
import { LatestHomeStockTitleIcon } from '../../components/inventory/LatestHomeStockTitleIcon';
import { InventoryCategoryFilterRow } from '../../components/inventory/InventoryCategoryFilterRow';
import { InventoryLocationFilterRow } from '../../components/inventory/InventoryLocationFilterRow';
import { inventoryStockLevelUiLabel } from '../../lib/inventoryStockLevelUiLabel';
import { resolveInventoryV2ExpiryBadge } from '../../lib/inventoryV2ExpiryBadge';
import { inventoryV2ExpiryBadgeNeedsDateHint } from '../../lib/inventoryV2ExpiryHint';
import { isWithinHelperEarlySubmitRange } from '../../utils/taskEarlySubmit';
import { PressableHint } from '../../components/ui/PressableHint';
import { PressableHintProvider } from '../../components/ui/PressableHintContext';
import { getHelperTodayYmd, getTodayYmdInTimeZone, resolveDisplayTimeZone } from '../../lib/userTodayYmd';
import { getFutureDueBannerMessage } from '../../utils/taskRelativeDueBanner';
import { useEmployerTaskDateStripPin } from '../../hooks/useEmployerTaskDateStripPin';
import { useTotalTaskCreationGate } from '../../hooks/useTotalTaskCreationGate';
import {
  isHelperHomeStockOverviewSearch,
  isTaskInstanceHelperHomeStockOverviewEligible,
  type HelperHomeStockOverviewLocationState,
  type InventoryWizardStartLocationState,
} from '../../lib/helperHomeStockOverview';
import {
  APPROVAL_STATUS,
  COMMENT_TARGET_TYPE,
  INVENTORY_LEVEL,
  INVENTORY_OPEN_EXPIRING_QUERY_PARAM,
  TASK_KIND,
  USER_LEVEL,
  USER_ROLE,
  isInventoryOpenExpiringSearch,
  resolveInventoryExpiringSoonWindowDays,
  shouldSurfaceInventoryExpiryForSummary,
  type InventoryLevel,
  type InventorySubmissionLine,
  type InventorySubmissionPayload,
  type TaskInstanceWithSubmission,
} from '@fdh/shared';
import {
  AutoTranslationHint,
  inventorySubmissionHasEmployerItemNameTranslations,
  resolveHelperInventoryCategoryDisplay,
  resolveInstanceDisplayNeedsUpdateReason,
  resolveInventoryItemNameForDisplay,
  resolveSubmissionItemCategoryDisplay,
  resolveSubmissionLocationNameDisplay,
  resolveSubmissionNoItemsCategoryDisplay,
  resolveTaskDisplayDescription,
  resolveTaskDisplayTitle,
  resolveTaskWizardCategoryDisplay,
  resolveTaskWizardLocationDisplay,
  resolveTranslationTargetLanguageLabel,
  taskInstanceHasHelperTranslations,
} from '../../components/translation';

const EXPIRY_YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** Item names longer than this use smaller type on Home Stock result cards. */
const INVENTORY_V2_LONG_ITEM_NAME_CHAR_THRESHOLD = 40;

/** Top-row clearance: category badge max width minus stock icon column (40px + gap). */
const INVENTORY_V2_ITEM_NAME_CATEGORY_BADGE_PR_CLASS =
  'max-md:pr-2 md:pr-[min(6.5rem,max(0px,calc(min(148px,46vw)-2.75rem)))]';

const inventoryLocationCoachmarkStorageKey = (taskInstanceId: string): string =>
  `fdh.taskReview.locationCoachmark.dismissed.${taskInstanceId}`;

type InventoryWizardLocation = NonNullable<NonNullable<InventorySubmissionPayload>['locations']>[number];
type LatestStockTaskCandidate = {
  taskInstanceId: string;
  title: string;
  dueDate: string;
  submittedAt: string;
  status: TaskInstanceWithSubmission['status'];
};

const formatDateInTimezone = (date: Date, timezone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  return `${year}-${month}-${day}`;
};

const addCalendarDaysToYmd = (ymd: string, deltaDays: number): string => {
  const [yRaw, mRaw, dRaw] = ymd.split('-');
  const y = Number(yRaw);
  const m = Number(mRaw);
  const d = Number(dRaw);
  const base = new Date(y, m - 1, d);
  base.setDate(base.getDate() + deltaDays);
  const yy = base.getFullYear();
  const mm = String(base.getMonth() + 1).padStart(2, '0');
  const dd = String(base.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

const getInventoryLevelBadgeClassName = (line: InventorySubmissionLine | null): string => {
  if (!line) {
    return 'shrink-0 rounded-full border border-amber-200/80 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-100';
  }
  if (line.status === INVENTORY_LEVEL.FULL) {
    return 'shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-200';
  }
  if (line.status === INVENTORY_LEVEL.HALF) {
    return 'shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-900 dark:bg-sky-900/50 dark:text-sky-200';
  }
  if (line.status === INVENTORY_LEVEL.LOW) {
    return 'shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/50 dark:text-amber-200';
  }
  if (line.status === INVENTORY_LEVEL.EMPTY) {
    return 'shrink-0 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-900 dark:bg-rose-900/50 dark:text-rose-200';
  }
  return 'shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-800 dark:text-gray-100';
};

export const TaskReviewPage = () => {
  const { t, language } = useLanguage();
  const { showSuccessToast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { guardNavigateToCreateTask } = useTotalTaskCreationGate();
  const isHighTierTaskKindLocked = user?.level === USER_LEVEL.FREE;
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const isLatestHomeStockMode = location.pathname === '/stock/latest-home-stock';
  const timezone = resolveDisplayTimeZone(user?.settings?.timezone);
  const todayYmd = useMemo(() => formatDateInTimezone(new Date(), timezone), [timezone]);
  const stockHistoryStartDate = useMemo(() => addCalendarDaysToYmd(todayYmd, -365), [todayYmd]);

  const [taskInstance, setTaskInstance] = useState<TaskInstanceWithSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [needsUpdateModalOpen, setNeedsUpdateModalOpen] = useState(false);
  const [needsUpdateReasonInput, setNeedsUpdateReasonInput] = useState('');
  const [needsUpdateModalError, setNeedsUpdateModalError] = useState<string | null>(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [stocktakePhotoIndex, setStocktakePhotoIndex] = useState(0);
  const [inventoryV2LocationIdx, setInventoryV2LocationIdx] = useState(0);
  const [inventoryV2PhotoIdx, setInventoryV2PhotoIdx] = useState(0);
  const [inventoryV2DeselectedCategories, setInventoryV2DeselectedCategories] = useState<Set<string>>(
    () => new Set(),
  );
  const [inventoryReviewSummary, setInventoryReviewSummary] = useState<{
    locations: Array<{
      locationName: string;
      photoCount: number;
      hasAnyItem: boolean;
      itemCount: number;
      categoriesWithItems: string[];
      categoriesNoItems: string[];
    }>;
  } | null>(null);
  const [inventoryReviewSummaryLoading, setInventoryReviewSummaryLoading] = useState(false);
  const [inventoryLocationCoachmarkOpen, setInventoryLocationCoachmarkOpen] = useState(false);
  const [inventoryV2StatModalKind, setInventoryV2StatModalKind] = useState<
    null | 'lowStock' | 'expiring'
  >(null);
  const [latestStockModalOpen, setLatestStockModalOpen] = useState(false);
  const [latestStockCandidates, setLatestStockCandidates] = useState<LatestStockTaskCandidate[]>([]);
  const [latestStockListLoading, setLatestStockListLoading] = useState(false);
  const [selectedLatestStockTaskInstanceId, setSelectedLatestStockTaskInstanceId] = useState<string | null>(null);
  const [showTaskContentOriginal, setShowTaskContentOriginal] = useState(false);
  const [showInventoryItemNamesOriginal, setShowInventoryItemNamesOriginal] = useState(false);
  const inventoryLocationSectionAnchorRef = useRef<HTMLDivElement>(null);
  const inventoryLocationSectionPanelRef = useRef<HTMLDivElement>(null);
  const effectiveTaskInstanceId = isLatestHomeStockMode ? selectedLatestStockTaskInstanceId : (id ?? null);

  useEffect(() => {
    setShowTaskContentOriginal(false);
    setShowInventoryItemNamesOriginal(false);
  }, [language]);

  const inventoryDisplayRows = useMemo((): {
    key: string;
    itemName: string;
    line: InventorySubmissionLine | null;
  }[] => {
    if (!taskInstance) {
      return [];
    }
    const { task, inventoryTemplate, inventorySubmission: invSub } = taskInstance;
    const inventoryLines = invSub?.lines ?? [];
    if (task.taskKind !== TASK_KIND.INVENTORY) {
      return [];
    }
    if (inventoryLines.length > 0) {
      return inventoryLines.map((line) => ({
        key: line.id,
        itemName: line.itemName,
        line,
      }));
    }
    return (inventoryTemplate ?? []).map((row) => ({
      key: row.itemId,
      itemName: row.itemName,
      line: null,
    }));
  }, [taskInstance]);

  const stocktakePhotoCount = useMemo(() => {
    if (!taskInstance || taskInstance.task.taskKind !== TASK_KIND.INVENTORY) {
      return 0;
    }
    const lines = taskInstance.inventorySubmission?.lines ?? [];
    const seen = new Set<string>();
    let n = 0;
    for (const line of lines) {
      const c = (line.photoUrl ?? '').trim();
      if (c === '') {
        continue;
      }
      if (seen.has(c)) {
        continue;
      }
      seen.add(c);
      n += 1;
    }
    return n;
  }, [taskInstance]);

  const sortedInventoryWizardLocations = useMemo((): InventoryWizardLocation[] => {
    const locs = taskInstance?.inventorySubmission?.locations;
    if (!locs || locs.length === 0) {
      return [];
    }
    return [...locs].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [taskInstance?.inventorySubmission?.locations]);

  const inventoryV2LocationFilterOptions = useMemo(
    () =>
      sortedInventoryWizardLocations.map((loc) => ({
        id: `${loc.locationName}-${loc.sortOrder}`,
        label:
          user?.role === USER_ROLE.HELPER
            ? resolveSubmissionLocationNameDisplay(loc, showTaskContentOriginal)
            : loc.locationName,
      })),
    [sortedInventoryWizardLocations, showTaskContentOriginal, user?.role],
  );

  const isInventoryWizardV2Ui = Boolean(
    taskInstance?.task.taskKind === TASK_KIND.INVENTORY && sortedInventoryWizardLocations.length > 0,
  );

  const {
    isPinned: isInventoryLocationSectionPinned,
    pinnedTop: inventoryLocationSectionPinnedTop,
    spacerHeightPx: inventoryLocationSectionSpacerHeightPx,
  } = useEmployerTaskDateStripPin(inventoryLocationSectionAnchorRef, inventoryLocationSectionPanelRef);

  const inventoryV2ViewerTodayYmd = useMemo(
    () => getTodayYmdInTimeZone(resolveDisplayTimeZone(user?.settings?.timezone)),
    [user?.settings?.timezone],
  );

  const inventoryV2AggregateStats = useMemo(() => {
    if (sortedInventoryWizardLocations.length === 0) {
      return null;
    }
    const tzToday = inventoryV2ViewerTodayYmd;
    const notifyExpiryLeadDays = taskInstance?.task.inventoryNotifyExpiryLeadDays;
    const expiringWindowDays = resolveInventoryExpiringSoonWindowDays(notifyExpiryLeadDays);

    type LowStockSummaryRow = {
      itemName: string;
      categoryName: string;
      locationName: string;
      stockLevel: InventoryLevel;
    };
    type ExpiringSummaryRow = {
      itemName: string;
      categoryName: string;
      locationName: string;
      detailLine: string;
    };

    const lowStockRows: LowStockSummaryRow[] = [];
    const expiringRows: ExpiringSummaryRow[] = [];

    for (const loc of sortedInventoryWizardLocations) {
      const locationName = (loc.locationName ?? '').trim();
      for (const item of loc.items) {
        const itemName = item.itemName.trim();
        const categoryName = item.categoryName.trim();

        if (item.stockLevel === INVENTORY_LEVEL.LOW || item.stockLevel === INVENTORY_LEVEL.EMPTY) {
          lowStockRows.push({ itemName, categoryName, locationName, stockLevel: item.stockLevel });
        }

        const qualifyingDates: string[] = [];
        for (const e of item.expiries ?? []) {
          if (e.isNoExpiry === true || e.expiryDate === 'NO_EXPIRY') {
            continue;
          }
          const d = e.expiryDate.trim();
          if (!EXPIRY_YMD_REGEX.test(d)) {
            continue;
          }
          if (!shouldSurfaceInventoryExpiryForSummary(tzToday, d, notifyExpiryLeadDays)) {
            continue;
          }
          qualifyingDates.push(d);
        }
        if (qualifyingDates.length > 0) {
          const sortedUnique = [...new Set(qualifyingDates)].sort((a, b) => a.localeCompare(b));
          const detailLine = sortedUnique
            .map((d) => {
              const { label } = resolveInventoryV2ExpiryBadge(t, tzToday, d);
              return `${d} (${label})`;
            })
            .join(' · ');
          expiringRows.push({ itemName, categoryName, locationName, detailLine });
        }
      }
    }

    const sortKey = (r: {
      locationName: string;
      categoryName: string;
      itemName: string;
    }): string => `${r.locationName}\u0000${r.categoryName}\u0000${r.itemName}`;
    lowStockRows.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    expiringRows.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

    return {
      lowStock: lowStockRows.length,
      expiringSoon: expiringRows.length,
      expiringWindowDays,
      lowStockRows,
      expiringRows,
    };
  }, [inventoryV2ViewerTodayYmd, sortedInventoryWizardLocations, t, taskInstance?.task.inventoryNotifyExpiryLeadDays]);

  const handleCloseInventoryV2StatModal = useCallback(() => {
    setInventoryV2StatModalKind(null);
  }, []);

  const inventoryV2TotalStocktakePhotosDeduped = useMemo(() => {
    const canon = new Set<string>();
    for (const loc of sortedInventoryWizardLocations) {
      for (const raw of loc.photoUrls) {
        const c = (raw ?? '').trim();
        if (c !== '') {
          canon.add(c);
        }
      }
    }
    return canon.size;
  }, [sortedInventoryWizardLocations]);

  const inventoryV2ResolvedLocationIdx = useMemo(() => {
    const n = sortedInventoryWizardLocations.length;
    if (n === 0) {
      return 0;
    }
    return Math.min(Math.max(0, inventoryV2LocationIdx), n - 1);
  }, [inventoryV2LocationIdx, sortedInventoryWizardLocations.length]);

  const inventoryV2CurrentLocation = useMemo(() => {
    if (sortedInventoryWizardLocations.length === 0) {
      return undefined;
    }
    return sortedInventoryWizardLocations[inventoryV2ResolvedLocationIdx];
  }, [sortedInventoryWizardLocations, inventoryV2ResolvedLocationIdx]);

  const inventoryV2CurrentLocationPhotoCount = useMemo(() => {
    const loc = inventoryV2CurrentLocation;
    if (!loc) {
      return 0;
    }
    return loc.photoUrls.filter((u) => (u ?? '').trim() !== '').length;
  }, [inventoryV2CurrentLocation]);

  const inventoryV2CategoryFilterEntries = useMemo(() => {
    const loc = inventoryV2CurrentLocation;
    const taskCategories = taskInstance?.task.inventoryCategories;
    if (!loc) {
      return [] as Array<{ category: string; emptyAtLocation: boolean; displayCategory?: string }>;
    }
    const categoriesWithItems = new Set<string>();
    for (const it of loc.items) {
      const c = it.categoryName.trim();
      if (c !== '') {
        categoriesWithItems.add(c);
      }
    }
    const explicitNoItemCats = new Set<string>(
      (loc.noItemsCategories ?? []).map((c) => c.trim()).filter((c) => c !== ''),
    );
    for (const c of categoriesWithItems) {
      explicitNoItemCats.delete(c);
    }
    const present = new Set<string>([...categoriesWithItems, ...explicitNoItemCats]);
    const taskOrder =
      Array.isArray(taskCategories) && taskCategories.length > 0
        ? taskCategories.map((c) => String(c).trim()).filter((c) => c !== '')
        : [];

    const orderedNames: string[] = [];
    const seen = new Set<string>();
    if (taskOrder.length > 0) {
      for (const c of taskOrder) {
        if (!present.has(c) || seen.has(c)) {
          continue;
        }
        orderedNames.push(c);
        seen.add(c);
      }
    }
    const restSorted = [...present].filter((c) => !seen.has(c)).sort((a, b) => a.localeCompare(b));
    orderedNames.push(...restSorted);

    return orderedNames.map((category) => ({
      category,
      emptyAtLocation: !categoriesWithItems.has(category),
      displayCategory:
        user?.role === USER_ROLE.HELPER && taskInstance
          ? resolveHelperInventoryCategoryDisplay({
              category,
              loc,
              task: taskInstance.task,
              items: loc.items,
              showOriginal: showTaskContentOriginal,
            })
          : undefined,
    }));
  }, [
    inventoryV2CurrentLocation,
    showTaskContentOriginal,
    taskInstance,
    user?.role,
  ]);

  const inventoryV2SelectedEmptyCategories = useMemo(() => {
    return inventoryV2CategoryFilterEntries.filter(
      (e) => e.emptyAtLocation && !inventoryV2DeselectedCategories.has(e.category),
    );
  }, [inventoryV2CategoryFilterEntries, inventoryV2DeselectedCategories]);

  const inventoryV2TaskCategoryBadgeIndexMap = useMemo(() => {
    const taskCategories = taskInstance?.task.inventoryCategories;
    const ordered =
      Array.isArray(taskCategories) && taskCategories.length > 0
        ? taskCategories.map((c) => String(c).trim()).filter((c) => c !== '')
        : [];
    return buildInventoryV2CategoryBadgeIndexMap(ordered);
  }, [taskInstance?.task.inventoryCategories]);

  const inventoryV2VisibleItems = useMemo(() => {
    const loc = inventoryV2CurrentLocation;
    if (!loc) {
      return [];
    }
    return [...loc.items]
      .filter((it) => !inventoryV2DeselectedCategories.has(it.categoryName.trim()))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [inventoryV2CurrentLocation, inventoryV2DeselectedCategories]);

  const gpsPreviewCoords = useMemo(() => {
    const sub = taskInstance?.submission;
    if (!sub?.gpsEnabled || sub.gpsLat === '' || sub.gpsLng === '') {
      return null;
    }
    const lat = Number(sub.gpsLat);
    const lng = Number(sub.gpsLng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }
    return { lat, lng };
  }, [taskInstance?.submission]);

  const fetchLatestHomeStockCandidates = useCallback(async () => {
    if (!isLatestHomeStockMode) {
      return;
    }
    setLatestStockListLoading(true);
    setError(null);
    try {
      const [pendingResponse, needsUpdateResponse, approvedResponse] = await Promise.all([
        taskInstanceApi.history(stockHistoryStartDate, todayYmd, APPROVAL_STATUS.PENDING),
        taskInstanceApi.history(stockHistoryStartDate, todayYmd, APPROVAL_STATUS.NEEDS_UPDATE),
        taskInstanceApi.history(stockHistoryStartDate, todayYmd, APPROVAL_STATUS.APPROVED),
      ]);
      const allTasks = [
        ...(pendingResponse.data?.tasks ?? []),
        ...(needsUpdateResponse.data?.tasks ?? []),
        ...(approvedResponse.data?.tasks ?? []),
      ];
      const candidates = allTasks
        .filter((task) => task.taskKind === TASK_KIND.INVENTORY)
        .filter((task) => String(task.submittedAt ?? '').trim() !== '')
        .sort((a, b) => String(b.submittedAt).localeCompare(String(a.submittedAt)))
        .map((task) => ({
          taskInstanceId: task.taskInstanceId,
          title: task.title,
          dueDate: task.dueDate,
          submittedAt: task.submittedAt,
          status: task.status,
        }));
      setLatestStockCandidates(candidates);
      if (!selectedLatestStockTaskInstanceId && candidates.length > 0) {
        setSelectedLatestStockTaskInstanceId(candidates[0].taskInstanceId);
      }
      if (selectedLatestStockTaskInstanceId && !candidates.some((c) => c.taskInstanceId === selectedLatestStockTaskInstanceId)) {
        setSelectedLatestStockTaskInstanceId(candidates[0]?.taskInstanceId ?? null);
      }
    } catch {
      setError(null);
      setLatestStockCandidates([]);
      setSelectedLatestStockTaskInstanceId(null);
    } finally {
      setLatestStockListLoading(false);
    }
  }, [
    isLatestHomeStockMode,
    selectedLatestStockTaskInstanceId,
    stockHistoryStartDate,
    t,
    todayYmd,
  ]);

  const fetchTaskInstance = useCallback(async () => {
    if (!effectiveTaskInstanceId || !user?.id) {
      if (!effectiveTaskInstanceId) {
        setTaskInstance(null);
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError(null);

    const handleLatestModeLoadFailure = (): void => {
      if (!isLatestHomeStockMode) {
        setError(t('task.review.loadError'));
        return;
      }
      setError(null);
      if (!selectedLatestStockTaskInstanceId) {
        return;
      }
      setLatestStockCandidates((prev) => {
        const next = prev.filter((candidate) => candidate.taskInstanceId !== selectedLatestStockTaskInstanceId);
        if (next.length === 0) {
          setTaskInstance(null);
          setSelectedLatestStockTaskInstanceId(null);
          return next;
        }
        setSelectedLatestStockTaskInstanceId(next[0].taskInstanceId);
        return next;
      });
    };

    try {
      const response = await taskInstanceApi.get(effectiveTaskInstanceId, language);
      if (response.success && response.data) {
        setTaskInstance(response.data);
      } else {
        if (!isLatestHomeStockMode) {
          setError(response.error ?? t('task.review.loadError'));
        } else {
          handleLatestModeLoadFailure();
        }
      }
    } catch {
      if (!isLatestHomeStockMode) {
        setError(t('task.review.loadError'));
      } else {
        handleLatestModeLoadFailure();
      }
    } finally {
      setLoading(false);
    }
  }, [effectiveTaskInstanceId, isLatestHomeStockMode, language, selectedLatestStockTaskInstanceId, t, user?.id]);

  useEffect(() => {
    if (!isLatestHomeStockMode) {
      return;
    }
    void fetchLatestHomeStockCandidates();
  }, [fetchLatestHomeStockCandidates, isLatestHomeStockMode]);

  useEffect(() => {
    void fetchTaskInstance();
  }, [fetchTaskInstance, location.pathname]);

  useEffect(() => {
    if (!effectiveTaskInstanceId || !taskInstance || taskInstance.task.taskKind !== TASK_KIND.INVENTORY) {
      setInventoryReviewSummary(null);
      setInventoryReviewSummaryLoading(false);
      return;
    }
    const loadInventoryReviewSummary = async () => {
      setInventoryReviewSummaryLoading(true);
      try {
        const response = await inventoryApi.getSubmissionReviewSummary(effectiveTaskInstanceId);
        if (response.success && response.data) {
          setInventoryReviewSummary(response.data);
          return;
        }
        setInventoryReviewSummary(null);
      } catch {
        setInventoryReviewSummary(null);
      } finally {
        setInventoryReviewSummaryLoading(false);
      }
    };
    void loadInventoryReviewSummary();
  }, [effectiveTaskInstanceId, taskInstance]);

  useEffect(() => {
    setStocktakePhotoIndex((prev) => {
      if (stocktakePhotoCount <= 0) {
        return 0;
      }
      return Math.min(prev, stocktakePhotoCount - 1);
    });
  }, [stocktakePhotoCount, effectiveTaskInstanceId]);

  useEffect(() => {
    setInventoryV2LocationIdx(0);
    setInventoryV2PhotoIdx(0);
    setInventoryV2DeselectedCategories(new Set());
  }, [effectiveTaskInstanceId]);

  useEffect(() => {
    if (!isInventoryWizardV2Ui) {
      setInventoryV2StatModalKind(null);
    }
  }, [isInventoryWizardV2Ui]);

  useEffect(() => {
    if (sortedInventoryWizardLocations.length === 0) {
      return;
    }
    setInventoryV2LocationIdx((idx) => Math.min(idx, sortedInventoryWizardLocations.length - 1));
  }, [sortedInventoryWizardLocations.length]);

  useEffect(() => {
    setInventoryV2PhotoIdx(0);
    setInventoryV2DeselectedCategories(new Set());
  }, [inventoryV2LocationIdx]);

  useEffect(() => {
    const max = Math.max(0, inventoryV2CurrentLocationPhotoCount - 1);
    setInventoryV2PhotoIdx((i) => Math.min(i, max));
  }, [inventoryV2CurrentLocationPhotoCount]);

  useEffect(() => {
    if (!effectiveTaskInstanceId || !isInventoryWizardV2Ui || sortedInventoryWizardLocations.length <= 1) {
      setInventoryLocationCoachmarkOpen(false);
      return;
    }
    try {
      const dismissed = sessionStorage.getItem(inventoryLocationCoachmarkStorageKey(effectiveTaskInstanceId));
      setInventoryLocationCoachmarkOpen(dismissed !== '1');
    } catch {
      setInventoryLocationCoachmarkOpen(true);
    }
  }, [effectiveTaskInstanceId, isInventoryWizardV2Ui, sortedInventoryWizardLocations.length]);

  const helperHomeStockOverviewFromState = (
    location.state as HelperHomeStockOverviewLocationState | null
  )?.helperHomeStockOverview;
  const showHelperHomeStockOverview = Boolean(
    user?.role === USER_ROLE.HELPER &&
      taskInstance &&
      (isHelperHomeStockOverviewSearch(location.search) || helperHomeStockOverviewFromState === true) &&
      isTaskInstanceHelperHomeStockOverviewEligible(taskInstance),
  );

  const handleHelperStartHomeStockWizard = useCallback(() => {
    if (!effectiveTaskInstanceId) {
      return;
    }
    navigate(`/tasks/inventory/${effectiveTaskInstanceId}`, {
      state: { startSubmitWizard: true } satisfies InventoryWizardStartLocationState,
    });
  }, [effectiveTaskInstanceId, navigate]);

  const handleHelperStartHomeStockWizardKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
      handleHelperStartHomeStockWizard();
    },
    [handleHelperStartHomeStockWizard],
  );

  useEffect(() => {
    if (!effectiveTaskInstanceId || !user || isLatestHomeStockMode) return;
    if (user.role !== USER_ROLE.HELPER) return;
    if (!taskInstance) return;
    if (taskInstance.isFromPreviousHelper) return;
    if (showHelperHomeStockOverview) return;
    if (isInventoryOpenExpiringSearch(location.search)) return;

    const todayYmd = getHelperTodayYmd(user.settings?.timezone);
    if (
      taskInstance.dueDate > todayYmd &&
      !isWithinHelperEarlySubmitRange(taskInstance.dueDate, todayYmd)
    ) {
      return;
    }

    if (taskInstance.task.taskKind === TASK_KIND.INVENTORY) {
      if (
        taskInstance.status === APPROVAL_STATUS.PENDING &&
        (!taskInstance.submission ||
          !taskInstance.submission.submittedAt ||
          String(taskInstance.submission.submittedAt).trim() === '')
      ) {
        navigate(`/tasks/inventory/${effectiveTaskInstanceId}`, { replace: true });
        return;
      }
      if (taskInstance.status === APPROVAL_STATUS.NEEDS_UPDATE) {
        navigate(`/tasks/inventory/${effectiveTaskInstanceId}`, { replace: true });
      }
      return;
    }

    if (
      taskInstance.status === APPROVAL_STATUS.PENDING &&
      (!taskInstance.submission ||
        !taskInstance.submission.submittedAt ||
        String(taskInstance.submission.submittedAt).trim() === '')
    ) {
      navigate(`/tasks/complete/${effectiveTaskInstanceId}`, { replace: true });
      return;
    }
    if (taskInstance.status === APPROVAL_STATUS.NEEDS_UPDATE) {
      navigate(`/tasks/resubmit/${effectiveTaskInstanceId}`, { replace: true });
    }
  }, [effectiveTaskInstanceId, isLatestHomeStockMode, location.search, navigate, showHelperHomeStockOverview, taskInstance, user]);

  useEffect(() => {
    if (loading || !isInventoryWizardV2Ui) {
      return;
    }
    if (searchParams.get(INVENTORY_OPEN_EXPIRING_QUERY_PARAM) !== '1') {
      return;
    }
    setInventoryV2StatModalKind('expiring');
    const next = new URLSearchParams(searchParams);
    next.delete(INVENTORY_OPEN_EXPIRING_QUERY_PARAM);
    setSearchParams(next, { replace: true });
  }, [isInventoryWizardV2Ui, loading, searchParams, setSearchParams]);

  const handleApproveReview = useCallback(async () => {
    if (!effectiveTaskInstanceId || !taskInstance) return;

    setReviewing(true);
    setError(null);

    try {
      const response = await taskInstanceApi.review(effectiveTaskInstanceId, {
        status: APPROVAL_STATUS.APPROVED,
        version: taskInstance.version,
        needsUpdateReason: '',
      });

      if (response.success) {
        showSuccessToast(t('toast.task.reviewedSuccess'));
        if (isLatestHomeStockMode) {
          // Review API returns TaskInstance only (no task/submission); refetch full detail.
          await fetchTaskInstance();
          await fetchLatestHomeStockCandidates();
        } else {
          navigate('/tasks', { replace: true });
        }
        return;
      }
      if (response.error?.includes('409') || response.error?.includes('conflict')) {
        setError(t('task.review.versionConflict'));
        return;
      }
      setError(response.error ?? t('task.review.reviewError'));
    } catch (err) {
      if (isLimitReachedError(err)) {
        return;
      }
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage.includes('409')) {
        setError(t('task.review.versionConflict'));
        return;
      }
      setError(t('task.review.reviewError'));
    } finally {
      setReviewing(false);
    }
  }, [
    effectiveTaskInstanceId,
    fetchLatestHomeStockCandidates,
    fetchTaskInstance,
    isLatestHomeStockMode,
    navigate,
    showSuccessToast,
    t,
    taskInstance,
  ]);

  const handleOpenNeedsUpdateModal = useCallback(() => {
    setNeedsUpdateModalError(null);
    setNeedsUpdateReasonInput('');
    setNeedsUpdateModalOpen(true);
  }, []);

  const handleCloseNeedsUpdateModal = useCallback(() => {
    if (reviewing) return;
    setNeedsUpdateModalOpen(false);
  }, [reviewing]);

  const handleConfirmNeedsUpdateReview = useCallback(async () => {
    if (!effectiveTaskInstanceId || !taskInstance) return;

    setReviewing(true);
    setNeedsUpdateModalError(null);
    setError(null);

    try {
      const response = await taskInstanceApi.review(effectiveTaskInstanceId, {
        status: APPROVAL_STATUS.NEEDS_UPDATE,
        version: taskInstance.version,
        needsUpdateReason: needsUpdateReasonInput,
      });

      if (response.success) {
        showSuccessToast(t('toast.task.needUpdateSuccess'));
        setNeedsUpdateModalOpen(false);
        if (isLatestHomeStockMode) {
          // Review API returns TaskInstance only (no task/submission); refetch full detail.
          await fetchTaskInstance();
          await fetchLatestHomeStockCandidates();
        } else {
          navigate('/tasks/pending-review', { replace: true });
        }
        return;
      }
      if (response.error?.includes('409') || response.error?.includes('conflict')) {
        setNeedsUpdateModalError(t('task.review.versionConflict'));
        return;
      }
      setNeedsUpdateModalError(response.error ?? t('task.review.reviewError'));
    } catch (err) {
      if (isLimitReachedError(err)) {
        return;
      }
      if (err instanceof ApiClientError && err.code === 'TASK_NEEDS_UPDATE_REASON_REQUIRED') {
        const minRaw = err.body.minLength;
        const minLen = typeof minRaw === 'number' ? minRaw : 1;
        setNeedsUpdateModalError(t('task.review.needsUpdateReasonTooShort', { min: minLen }));
        return;
      }
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage.includes('409')) {
        setNeedsUpdateModalError(t('task.review.versionConflict'));
        return;
      }
      setNeedsUpdateModalError(t('task.review.reviewError'));
    } finally {
      setReviewing(false);
    }
  }, [
    effectiveTaskInstanceId,
    fetchLatestHomeStockCandidates,
    fetchTaskInstance,
    isLatestHomeStockMode,
    navigate,
    needsUpdateReasonInput,
    showSuccessToast,
    t,
    taskInstance,
  ]);

  const handleBack = () => {
    if (user?.role === USER_ROLE.EMPLOYER) {
      const fallbackPath = isLatestHomeStockMode
        ? '/dashboard'
        : location.pathname.endsWith('/review')
          ? '/tasks/pending-review'
          : '/tasks';
      navigateBack(navigate, fallbackPath);
      return;
    }
    navigate(-1);
  };

  const handleSelectLatestHomeStockTask = useCallback((taskInstanceId: string) => {
    setSelectedLatestStockTaskInstanceId(taskInstanceId);
    setLatestStockModalOpen(false);
  }, []);

  const handleAddHomeStockTaskFabClick = useCallback(() => {
    if (isHighTierTaskKindLocked) {
      emitTaskKindNotAllowed();
      return;
    }
    void guardNavigateToCreateTask(navigate, '/tasks/new/inventory');
  }, [guardNavigateToCreateTask, isHighTierTaskKindLocked, navigate]);

  const handlePrevPhoto = () => {
    setCurrentPhotoIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextPhoto = () => {
    if (!taskInstance?.submission) return;
    const maxIndex = taskInstance.submission.photoUrls.length - 1;
    setCurrentPhotoIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  const handlePrevStocktakePhoto = () => {
    setStocktakePhotoIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextStocktakePhoto = () => {
    setStocktakePhotoIndex((prev) => {
      const max = Math.max(0, stocktakePhotoCount - 1);
      return Math.min(max, prev + 1);
    });
  };

  const handlePrevInventoryV2Photo = useCallback(() => {
    setInventoryV2PhotoIdx((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNextInventoryV2Photo = useCallback(() => {
    setInventoryV2PhotoIdx((prev) => {
      const max = Math.max(0, inventoryV2CurrentLocationPhotoCount - 1);
      return Math.min(max, prev + 1);
    });
  }, [inventoryV2CurrentLocationPhotoCount]);

  const handleToggleInventoryV2Category = useCallback((category: string) => {
    setInventoryV2DeselectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  const handleDismissInventoryLocationCoachmark = useCallback(() => {
    if (effectiveTaskInstanceId) {
      try {
        sessionStorage.setItem(inventoryLocationCoachmarkStorageKey(effectiveTaskInstanceId), '1');
      } catch {
        /* ignore */
      }
    }
    setInventoryLocationCoachmarkOpen(false);
  }, [effectiveTaskInstanceId]);

  useEffect(() => {
    if (!inventoryLocationCoachmarkOpen) {
      return undefined;
    }
    const onEscapeKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      handleDismissInventoryLocationCoachmark();
    };
    window.addEventListener('keydown', onEscapeKey);
    return () => window.removeEventListener('keydown', onEscapeKey);
  }, [inventoryLocationCoachmarkOpen, handleDismissInventoryLocationCoachmark]);

  const handleInventoryLocationCoachmarkCloseKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
      handleDismissInventoryLocationCoachmark();
    },
    [handleDismissInventoryLocationCoachmark],
  );

  const handleSelectInventoryV2Location = useCallback((index: number) => {
    setInventoryV2LocationIdx(index);
  }, []);

  if (isLatestHomeStockMode && latestStockListLoading && latestStockCandidates.length === 0) {
    return (
      <div className="flex min-h-full items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
      </div>
    );
  }

  const showEmployerLatestHomeStockFab =
    isLatestHomeStockMode && user?.role === USER_ROLE.EMPLOYER;

  const latestHomeStockMenuButton = (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setLatestStockModalOpen(true)}
        tabIndex={0}
        className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200/80 bg-white text-gray-500 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
        aria-label={t('task.review.latestHomeStockOpenRecentAria')}
        aria-haspopup="dialog"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={3}>
            <circle cx="12" cy="12" r="1" />
            <circle cx="12" cy="5" r="1" />
            <circle cx="12" cy="19" r="1" />
          </g>
        </svg>
      </button>
    </div>
  );

  if (isLatestHomeStockMode && !latestStockListLoading && latestStockCandidates.length === 0) {
    return (
      <div className="min-h-full bg-[#f7f8fb] px-4 py-6 pb-24 dark:bg-gray-950">
        <div className="mx-auto max-w-lg">
          <AppPageTitleSection
            title={t('task.review.latestHomeStockTitle')}
            endDecoration={<LatestHomeStockTitleIcon />}
          >
            {latestHomeStockMenuButton}
          </AppPageTitleSection>
          <div className="rounded-xl border border-dashed border-gray-300 bg-white p-5 text-center dark:border-gray-700 dark:bg-gray-900">
            <p className="text-sm text-gray-700 dark:text-gray-200">
              {user?.role === USER_ROLE.EMPLOYER
                ? t('task.review.latestHomeStockEmptyEmployer')
                : t('task.review.latestHomeStockEmptyHelper')}
            </p>
          </div>
        </div>
        {showEmployerLatestHomeStockFab ? (
          <PagePrimaryFab
            onClick={handleAddHomeStockTaskFabClick}
            ariaLabel={t('task.review.addHomeStockTaskFabAria')}
          >
            <span className="text-lg font-bold leading-none" aria-hidden>+</span>
            <span>{t('task.review.addHomeStockTaskFab')}</span>
          </PagePrimaryFab>
        ) : null}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
      </div>
    );
  }

  if (error || (!taskInstance && !isLatestHomeStockMode)) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center bg-gray-50 px-4 dark:bg-gray-950">
        {error ? (
          <p className="text-center text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        ) : null}
        {!isLatestHomeStockMode ? (
          <button
            type="button"
            onClick={handleBack}
            className="mt-4 text-primary-600 underline hover:no-underline focus:outline-none dark:text-primary-400"
          >
            {t('task.action.back')}
          </button>
        ) : null}
      </div>
    );
  }

  if (!taskInstance) {
    return (
      <div className="flex min-h-full items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
      </div>
    );
  }

  const { task, submission, inventorySubmission } = taskInstance;
  const photoCount = submission?.photoUrls?.length ?? 0;

  const wizardPlanLocationsPreview = task.inventoryLocations ?? [];
  const wizardPlanCategoriesPreview = task.inventoryCategories ?? [];
  const showInventoryWizardPlanPreviewOnReview =
    task.taskKind === TASK_KIND.INVENTORY &&
    (wizardPlanLocationsPreview.length > 0 || wizardPlanCategoriesPreview.length > 0);

  const isInventoryNotSubmitted =
    task.taskKind === TASK_KIND.INVENTORY &&
    taskInstance.status === APPROVAL_STATUS.PENDING &&
    !submission;

  const inventoryLocationSummaryHeading = isInventoryNotSubmitted
    ? t('task.review.inventoryTaskSummaryTitle')
    : t('task.review.inventoryLocationSummaryTitle');

  const gpsCoordsValid = gpsPreviewCoords !== null;
  const gpsLatNum = gpsPreviewCoords?.lat ?? 0;
  const gpsLngNum = gpsPreviewCoords?.lng ?? 0;
  const gpsAddress = submission?.gpsAddress?.trim() ?? '';

  const isFromPreviousHelper = taskInstance?.isFromPreviousHelper === true;

  const employerIsPaired = Boolean(user?.pairedAt && user.pairedAt.trim() !== '');
  const canReview =
    user?.role === USER_ROLE.EMPLOYER &&
    employerIsPaired &&
    !isFromPreviousHelper &&
    taskInstance.status === APPROVAL_STATUS.PENDING &&
    Boolean(submission);

  const showEmployerAddHomeStockFab = showEmployerLatestHomeStockFab && !canReview;

  const showHelperLatestHomeStockResubmitBar =
    isLatestHomeStockMode &&
    user?.role === USER_ROLE.HELPER &&
    !isFromPreviousHelper &&
    task.taskKind === TASK_KIND.INVENTORY &&
    taskInstance.status === APPROVAL_STATUS.NEEDS_UPDATE;

  const pageTitle = isLatestHomeStockMode
    ? t('task.review.latestHomeStockTitle')
    : task.taskKind === TASK_KIND.INVENTORY
      ? isInventoryNotSubmitted
        ? t('task.review.inventoryTaskSummaryTitle')
        : t('task.review.stockTakeDetailTitle')
      : user?.role === USER_ROLE.HELPER
        ? t('task.review.detailTitle')
        : t('task.review.title');

  const helperHasTranslations = taskInstanceHasHelperTranslations(taskInstance);
  const employerInventoryItemTranslations =
    inventorySubmissionHasEmployerItemNameTranslations(taskInstance);
  const helperTranslationTargetLabel = resolveTranslationTargetLanguageLabel(
    t,
    taskInstance.task.translation?.targetLanguage,
    language,
  );
  const employerItemTranslationTargetLabel = resolveTranslationTargetLanguageLabel(
    t,
    sortedInventoryWizardLocations
      .flatMap((loc) => loc.items)
      .find((item) => item.translation?.hasTranslation)?.translation?.targetLanguage,
    language,
  );

  const resolveAggregateRowItemName = (row: {
    itemName: string;
    categoryName: string;
    locationName: string;
  }): string => {
    const loc = sortedInventoryWizardLocations.find(
      (l) => (l.locationName ?? '').trim() === row.locationName.trim(),
    );
    const item = loc?.items.find(
      (it) =>
        it.itemName.trim() === row.itemName.trim() &&
        it.categoryName.trim() === row.categoryName.trim(),
    );
    if (!item) {
      return row.itemName;
    }
    return resolveInventoryItemNameForDisplay({
      item,
      viewerRole: user?.role ?? USER_ROLE.EMPLOYER,
      viewerLanguage: language,
      showOriginal:
        user?.role === USER_ROLE.HELPER
          ? showTaskContentOriginal
          : showInventoryItemNamesOriginal,
    });
  };

  const resolveAggregateRowLocationName = (locationName: string): string => {
    if (user?.role !== USER_ROLE.HELPER) {
      return locationName;
    }
    const loc = sortedInventoryWizardLocations.find(
      (l) => (l.locationName ?? '').trim() === locationName.trim(),
    );
    if (!loc) {
      return locationName;
    }
    return resolveSubmissionLocationNameDisplay(loc, showTaskContentOriginal);
  };

  const resolveAggregateRowCategoryName = (row: {
    categoryName: string;
    locationName: string;
  }): string => {
    if (user?.role !== USER_ROLE.HELPER) {
      return row.categoryName;
    }
    const loc = sortedInventoryWizardLocations.find(
      (l) => (l.locationName ?? '').trim() === row.locationName.trim(),
    );
    return resolveHelperInventoryCategoryDisplay({
      category: row.categoryName,
      loc,
      task,
      items: loc?.items ?? [],
      showOriginal: showTaskContentOriginal,
    });
  };

  const showHelperAwaitingEmployerBanner =
    user?.role === USER_ROLE.HELPER &&
    taskInstance.status === APPROVAL_STATUS.PENDING &&
    Boolean(
      submission &&
        submission.submittedAt &&
        String(submission.submittedAt).trim() !== ''
    );

  const helperTodayYmd =
    user?.role === USER_ROLE.HELPER ? getHelperTodayYmd(user?.settings?.timezone) : '';
  const employerTodayYmd =
    user?.role === USER_ROLE.EMPLOYER ? getTodayYmdInTimeZone(user?.settings?.timezone) : '';
  const viewerTodayYmd = helperTodayYmd || employerTodayYmd;
  const showHelperFutureOutOfRange =
    user?.role === USER_ROLE.HELPER &&
    Boolean(helperTodayYmd) &&
    taskInstance.dueDate > helperTodayYmd &&
    !isWithinHelperEarlySubmitRange(taskInstance.dueDate, helperTodayYmd);
  const futureDueBannerMessage =
    viewerTodayYmd && taskInstance.dueDate > viewerTodayYmd && !showHelperFutureOutOfRange
      ? getFutureDueBannerMessage(user?.role, viewerTodayYmd, taskInstance.dueDate, t)
      : null;

  const helperHomeStockOverviewCtaLabel =
    taskInstance.status === APPROVAL_STATUS.NEEDS_UPDATE
      ? t('task.review.helperResubmitHomeStock')
      : t('task.review.helperSubmitHomeStock');

  const latestHomeStockCardTranslationHint =
    isLatestHomeStockMode && user?.role === USER_ROLE.HELPER && helperHasTranslations ? (
      <AutoTranslationHint
        targetLanguageLabel={helperTranslationTargetLabel}
        showingOriginal={showTaskContentOriginal}
        onToggleOriginal={() => setShowTaskContentOriginal((prev) => !prev)}
      />
    ) : null;

  const taskInfoCardStatusCluster = (
    <div className="flex shrink-0 items-center gap-1.5">
      {latestHomeStockCardTranslationHint}
      {taskInstance.status === APPROVAL_STATUS.PENDING && !submission ? (
        <NotSubmittedStatusBadge label={t('task.status.notSubmitted')} />
      ) : (
        <StatusBadge status={taskInstance.status} />
      )}
    </div>
  );

  const renderHelperHomeStockOverviewCta = (): ReactNode => {
    if (!showHelperHomeStockOverview || showHelperFutureOutOfRange) {
      return null;
    }
    return (
      <div className="mt-4 border-t border-gray-200/80 pt-4 dark:border-gray-600">
        <AppButton
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          tabIndex={0}
          aria-label={helperHomeStockOverviewCtaLabel}
          onClick={handleHelperStartHomeStockWizard}
          onKeyDown={handleHelperStartHomeStockWizardKeyDown}
        >
          {helperHomeStockOverviewCtaLabel}
        </AppButton>
      </div>
    );
  };

  return (
    <div
      className={
        isLatestHomeStockMode
          ? 'min-h-full bg-[#f7f8fb] px-4 py-6 dark:bg-gray-950'
          : isInventoryWizardV2Ui
            ? 'min-h-full bg-[#f7f9fc] dark:bg-gray-950'
            : 'min-h-full bg-gray-50 dark:bg-gray-950'
      }
    >
      {isLatestHomeStockMode ? (
        <div className="mx-auto max-w-lg">
          <AppPageTitleSection title={pageTitle} endDecoration={<LatestHomeStockTitleIcon />}>
            {latestHomeStockMenuButton}
          </AppPageTitleSection>
        </div>
      ) : (
        <header
          className={
            isInventoryWizardV2Ui
              ? 'z-10 border-b border-gray-200/70 bg-[#f7f9fc]/95 backdrop-blur-md dark:border-gray-800 dark:bg-gray-950/95'
              : 'z-10 bg-white shadow-sm dark:bg-gray-900 dark:shadow-black/20'
          }
        >
          <div className="mx-auto max-w-lg px-4 py-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleBack}
                className={
                  isInventoryWizardV2Ui
                    ? 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200/80 bg-white text-gray-800 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/35 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800'
                    : 'flex h-10 w-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-800'
                }
                aria-label={t('task.action.back')}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <h1
                  className={
                    isInventoryWizardV2Ui
                      ? 'min-w-0 text-lg font-bold tracking-tight text-gray-900 dark:text-gray-50'
                      : 'min-w-0 text-xl font-bold text-gray-900 dark:text-gray-100'
                  }
                >
                  {pageTitle}
                </h1>
                {user?.role === USER_ROLE.HELPER && helperHasTranslations ? (
                  <AutoTranslationHint
                    targetLanguageLabel={helperTranslationTargetLabel}
                    showingOriginal={showTaskContentOriginal}
                    onToggleOriginal={() => setShowTaskContentOriginal((prev) => !prev)}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </header>
      )}

      <main
        className={`mx-auto max-w-lg space-y-6 ${isLatestHomeStockMode ? '' : 'px-4 py-6'} ${
          canReview
            ? SCROLL_BOTTOM_FOR_REVIEW_BAR
            : showEmployerAddHomeStockFab
              ? 'pb-32'
              : showHelperLatestHomeStockResubmitBar
                ? SCROLL_BOTTOM_FOR_REVIEW_AND_FAB
                : ''
        }`}
      >
        {isFromPreviousHelper ? <PreviousHelperBanner variant="task" /> : null}

        {showHelperFutureOutOfRange ? (
          <div
            className="flex items-center gap-2 rounded-xl bg-gray-100 px-4 py-3 text-gray-700 dark:bg-gray-800/80 dark:text-gray-200"
            role="status"
            aria-live="polite"
            aria-label={t('task.review.futureDueOutOfRange')}
          >
            <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="text-sm font-medium">{t('task.review.futureDueOutOfRange')}</span>
          </div>
        ) : null}

        {futureDueBannerMessage ? (
          <div
            className="flex items-center gap-2 rounded-xl bg-yellow-50 px-4 py-3 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-200"
            role="status"
            aria-live="polite"
            aria-label={futureDueBannerMessage}
          >
            <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span className="text-sm font-medium">{futureDueBannerMessage}</span>
          </div>
        ) : null}

        {showHelperAwaitingEmployerBanner ? (
          <div
            className="flex items-center gap-2 rounded-xl bg-yellow-50 px-4 py-3 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-200"
            role="status"
            aria-live="polite"
            aria-label={t('task.review.helperAwaitingEmployerBanner')}
          >
            <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm font-medium">{t('task.review.helperAwaitingEmployerBanner')}</span>
          </div>
        ) : null}

        {taskInstance.needsUpdateReason && String(taskInstance.needsUpdateReason).trim() !== '' ? (
          <div
            className="rounded-xl border border-red-100 bg-red-50/95 p-4 dark:border-red-900/40 dark:bg-red-950/35"
            role="region"
            aria-label={t('task.today.employerFeedbackLabel')}
          >
            <p className="text-xs font-semibold text-red-800 dark:text-red-200">
              {t('task.today.employerFeedbackLabel')}
            </p>
            <p className="mt-2 text-sm text-red-900 dark:text-red-100">
              {user?.role === USER_ROLE.HELPER
                ? resolveInstanceDisplayNeedsUpdateReason(
                    taskInstance.needsUpdateReason,
                    taskInstance.translation,
                    showTaskContentOriginal,
                  )
                : taskInstance.needsUpdateReason}
            </p>
          </div>
        ) : null}

        {user?.role === USER_ROLE.EMPLOYER && taskInstance.status === APPROVAL_STATUS.NEEDS_UPDATE ? (
          <div
            className="flex items-center gap-3 rounded-xl border border-amber-200/90 bg-amber-50 px-4 py-3 dark:border-amber-800/50 dark:bg-amber-950/40"
            role="status"
            aria-live="polite"
            aria-label={t('task.review.needsUpdatePhotoHint')}
          >
            <svg
              className="h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm font-medium text-amber-900 dark:text-amber-100">
              {t('task.review.needsUpdatePhotoHint')}
            </span>
          </div>
        ) : null}

        {/* Task Info Card */}
        {isInventoryWizardV2Ui ? (
          <div className="rounded-2xl border border-gray-200/80 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                  {resolveTaskDisplayTitle(task, showTaskContentOriginal)}
                </h2>
                {task.description ? (
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-600 dark:text-gray-300">
                    {resolveTaskDisplayDescription(task, showTaskContentOriginal)}
                  </p>
                ) : null}
              </div>
              {taskInfoCardStatusCluster}
            </div>

            <div className="mt-4 space-y-2 border-t border-gray-200 pt-4 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-300">
              <div className="flex justify-between">
                <span>{t('task.review.taskDate')}</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{taskInstance.dueDate}</span>
              </div>
              {task.dueTime ? (
                <div className="flex justify-between">
                  <span>{t('task.form.dueTimeLabel')}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{task.dueTime}</span>
                </div>
              ) : null}
              {inventorySubmission?.completedAt ? (
                <div className="flex justify-between">
                  <span>{t('task.review.completedAt')}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {inventorySubmission.completedAt}
                  </span>
                </div>
              ) : null}
              {submission?.submittedAt ? (
                <div className="flex justify-between">
                  <span>{t('task.review.submittedAt')}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{submission.submittedAt}</span>
                </div>
              ) : null}
            </div>
            {inventoryV2AggregateStats ? (
              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={() => setInventoryV2StatModalKind('lowStock')}
                  className="relative flex flex-1 flex-col items-center gap-1 rounded-2xl border border-amber-200/80 bg-gradient-to-br from-white via-amber-50/80 to-orange-100/70 p-3 text-center shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/35 dark:border-amber-800/50 dark:bg-gradient-to-br dark:from-gray-900 dark:via-amber-950/35 dark:to-gray-900"
                  aria-label={t('inventory.statDetailModal.openLowStockAria', {
                    count: inventoryV2AggregateStats.lowStock,
                  })}
                >
                  <InventorySummaryStatCardChevron />
                  <div className="rounded-xl bg-gray-100 p-2 text-amber-600 dark:bg-gray-800 dark:text-amber-400">
                    <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
                    </svg>
                  </div>
                  <span className="text-xl font-extrabold text-gray-900 dark:text-gray-50">
                    {inventoryV2AggregateStats.lowStock}
                  </span>
                  <span className="px-1 text-xs font-bold leading-none tracking-tight text-gray-500 dark:text-gray-400">
                    {t('task.review.inventoryStatLowStockCaption')}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setInventoryV2StatModalKind('expiring')}
                  className="relative flex flex-1 flex-col items-center gap-1 rounded-2xl border border-rose-200/80 bg-gradient-to-br from-white via-rose-50/80 to-red-100/70 p-3 text-center shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/35 dark:border-rose-800/50 dark:bg-gradient-to-br dark:from-gray-900 dark:via-rose-950/35 dark:to-gray-900"
                  aria-label={t('inventory.statDetailModal.openExpiringAria', {
                    count: inventoryV2AggregateStats.expiringSoon,
                  })}
                >
                  <InventorySummaryStatCardChevron />
                  <div className="rounded-xl bg-gray-100 p-2 text-red-600 dark:bg-gray-800 dark:text-red-400">
                    <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <span className="text-xl font-extrabold text-gray-900 dark:text-gray-50">
                    {inventoryV2AggregateStats.expiringSoon}
                  </span>
                  <span className="px-1 text-xs font-bold leading-none tracking-tight text-gray-500 dark:text-gray-400">
                    {t('task.review.inventoryStatExpiringCaption')}
                  </span>
                </button>
                <div
                  role="group"
                  aria-label={`${t('task.review.inventoryStatTotalPhotosCaption')}: ${inventoryV2TotalStocktakePhotosDeduped}`}
                  className="flex flex-1 flex-col items-center gap-1 rounded-2xl border border-emerald-200/80 bg-gradient-to-br from-white via-emerald-50/80 to-emerald-100/80 p-3 text-center shadow-sm dark:border-emerald-800/50 dark:bg-gradient-to-br dark:from-gray-900 dark:via-emerald-950/35 dark:to-gray-900"
                >
                  <div className="rounded-xl bg-gray-100 p-2 text-primary-600 dark:bg-gray-800 dark:text-emerald-400">
                    <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <span className="text-xl font-extrabold text-gray-900 dark:text-gray-50">
                    {inventoryV2TotalStocktakePhotosDeduped}
                  </span>
                  <span className="px-1 text-xs font-bold leading-none tracking-tight text-gray-500 dark:text-gray-400">
                    {t('task.review.inventoryStatTotalPhotosCaption')}
                  </span>
                </div>
              </div>
            ) : null}
            {renderHelperHomeStockOverviewCta()}
          </div>
        ) : (
          <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/20">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                  {resolveTaskDisplayTitle(task, showTaskContentOriginal)}
                </h2>
                {task.description ? (
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-600 dark:text-gray-300">
                    {resolveTaskDisplayDescription(task, showTaskContentOriginal)}
                  </p>
                ) : null}
              </div>
              {taskInfoCardStatusCluster}
            </div>

            <div className="mt-4 space-y-2 border-t border-gray-200 pt-4 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-300">
              <div className="flex justify-between">
                <span>{t('task.review.taskDate')}</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{taskInstance.dueDate}</span>
              </div>
              {task.dueTime ? (
                <div className="flex justify-between">
                  <span>{t('task.form.dueTimeLabel')}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{task.dueTime}</span>
                </div>
              ) : null}
              {task.taskKind === TASK_KIND.INVENTORY && inventorySubmission ? (
                <div className="flex justify-between">
                  <span>{t('task.review.completedAt')}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {inventorySubmission.completedAt}
                  </span>
                </div>
              ) : submission ? (
                <div className="flex justify-between">
                  <span>{t('task.review.completedAt')}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{submission.completedAt}</span>
                </div>
              ) : null}
              {submission ? (
                <div className="flex justify-between">
                  <span>{t('task.review.submittedAt')}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{submission.submittedAt}</span>
                </div>
              ) : null}
            </div>
            {renderHelperHomeStockOverviewCta()}
          </div>
        )}

        {isInventoryWizardV2Ui ? (
          <div className="space-y-5">
            <div ref={inventoryLocationSectionAnchorRef}>
              {isInventoryLocationSectionPinned && inventoryLocationSectionSpacerHeightPx > 0 ? (
                <div
                  className="pointer-events-none"
                  style={{ height: inventoryLocationSectionSpacerHeightPx }}
                  aria-hidden
                />
              ) : null}
              {!isInventoryLocationSectionPinned &&
              inventoryLocationCoachmarkOpen &&
              sortedInventoryWizardLocations.length > 1 ? (
                <div
                  className="-mx-4 mb-2 flex justify-center px-5"
                  role="dialog"
                  aria-modal="false"
                  aria-labelledby="inventory-v2-location-coachmark-desc"
                >
                  <div className="relative w-full max-w-[min(20rem,calc(100vw-2rem))] rounded-xl border border-gray-200 bg-white px-3 pb-3 pt-2 shadow-lg dark:border-gray-600 dark:bg-gray-900 dark:shadow-black/40">
                    <button
                      type="button"
                      tabIndex={0}
                      aria-label={t('task.review.inventoryLocationsCoachmarkDismissAria')}
                      onClick={handleDismissInventoryLocationCoachmark}
                      onKeyDown={handleInventoryLocationCoachmarkCloseKeyDown}
                      className="absolute right-1.5 top-1.5 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500/35 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <p
                      id="inventory-v2-location-coachmark-desc"
                      className="pr-9 text-left text-sm leading-snug text-gray-900 dark:text-gray-50"
                    >
                      {t('task.review.inventoryLocationsFilterReminder')}
                    </p>
                    <div
                      className="pointer-events-none absolute left-1/2 top-full flex -translate-x-1/2 flex-col items-center text-white dark:text-gray-900"
                      aria-hidden
                    >
                      <svg width="22" height="11" viewBox="0 0 22 11" className="drop-shadow-sm">
                        <path fill="currentColor" d="M11 11 L1 0 H21 Z" />
                      </svg>
                    </div>
                  </div>
                </div>
              ) : null}
              <div
                ref={inventoryLocationSectionPanelRef}
                className={
                  isInventoryLocationSectionPinned
                    ? 'fixed left-1/2 isolate z-[15] w-full max-w-lg -translate-x-1/2 border-b border-gray-200/70 bg-[#f7f9fc] px-4 py-3 shadow-sm backdrop-blur-md dark:border-gray-800 dark:bg-gray-950'
                    : '-mx-4 border-b border-gray-200/70 bg-[#f7f9fc]/95 px-4 py-3 backdrop-blur-md dark:border-gray-800 dark:bg-gray-950/95'
                }
                style={isInventoryLocationSectionPinned ? { top: inventoryLocationSectionPinnedTop } : undefined}
              >
                <div className="flex flex-col gap-2">
                  <div className="px-1">
                    <h3 className="text-base font-bold text-gray-900 dark:text-gray-50">
                      {t('task.review.inventoryLocationsSectionHeading')}
                    </h3>
                    {sortedInventoryWizardLocations.length > 1 && !inventoryLocationCoachmarkOpen ? (
                      <p id="inventory-v2-location-filter-hint" className="sr-only">
                        {t('task.review.inventoryLocationsFilterReminder')}
                      </p>
                    ) : null}
                  </div>
                  <div className="relative">
                    <InventoryLocationFilterRow
                      options={inventoryV2LocationFilterOptions}
                      selectedIndex={inventoryV2LocationIdx}
                      onSelect={handleSelectInventoryV2Location}
                      ariaLabel={t('task.review.inventoryLocationTabsAria')}
                      ariaDescribedBy={
                        sortedInventoryWizardLocations.length > 1
                          ? inventoryLocationCoachmarkOpen
                            ? 'inventory-v2-location-coachmark-desc'
                            : 'inventory-v2-location-filter-hint'
                          : undefined
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/20">
              <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('task.review.photos')}
              </h3>
              {inventoryV2CurrentLocationPhotoCount === 0 || !effectiveTaskInstanceId ? (
                <p className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  {t('task.review.inventoryLocationPhotosEmpty')}
                </p>
              ) : (
                <>
                  <div className="relative">
                    <div className="aspect-square overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
                      <ReceiptImageLightbox
                        inventoryV2LocationPhoto={{
                          taskInstanceId: effectiveTaskInstanceId,
                          locationIndex: inventoryV2ResolvedLocationIdx,
                          photoIndex: inventoryV2PhotoIdx,
                        }}
                        alt={`${t('task.review.photos')} ${inventoryV2PhotoIdx + 1}`}
                        enlargeAriaLabel={t('inventory.enlargePhotoAria')}
                        closeLabel={t('common.closeViewer')}
                        thumbButtonClassName="relative block h-full w-full overflow-hidden rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-primary-500"
                        thumbImgClassName="h-full w-full object-contain"
                      />
                    </div>
                    {inventoryV2CurrentLocationPhotoCount > 1 ? (
                      <>
                        <button
                          type="button"
                          onClick={handlePrevInventoryV2Photo}
                          disabled={inventoryV2PhotoIdx === 0}
                          className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-opacity hover:bg-black/70 disabled:opacity-30"
                          aria-label={t('task.review.prevPhoto')}
                        >
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={handleNextInventoryV2Photo}
                          disabled={inventoryV2PhotoIdx === inventoryV2CurrentLocationPhotoCount - 1}
                          className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-opacity hover:bg-black/70 disabled:opacity-30"
                          aria-label={t('task.review.nextPhoto')}
                        >
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </>
                    ) : null}
                    <div className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs text-white">
                      {t('task.review.photoCount', {
                        current: inventoryV2PhotoIdx + 1,
                        total: inventoryV2CurrentLocationPhotoCount,
                      })}
                    </div>
                  </div>
                  {inventoryV2CurrentLocationPhotoCount > 1 ? (
                    <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                      {Array.from({ length: inventoryV2CurrentLocationPhotoCount }, (_, index) => (
                        <button
                          key={`inv-v2-thumb-${inventoryV2ResolvedLocationIdx}-${index}`}
                          type="button"
                          onClick={() => setInventoryV2PhotoIdx(index)}
                          className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            index === inventoryV2PhotoIdx
                              ? 'border-primary-500'
                              : 'border-transparent opacity-60 hover:opacity-100'
                          }`}
                          aria-label={t('task.review.photoCount', {
                            current: index + 1,
                            total: inventoryV2CurrentLocationPhotoCount,
                          })}
                          aria-pressed={index === inventoryV2PhotoIdx}
                        >
                          <TaskInstanceInventoryPhotoImg
                            taskInstanceId={effectiveTaskInstanceId}
                            variant="v2Location"
                            locationIndex={inventoryV2ResolvedLocationIdx}
                            photoIndex={index}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <h3 className="px-1 text-base font-bold text-gray-900 dark:text-gray-50">
                {t('task.review.inventoryFilterCategoriesHeading')}
              </h3>
              {inventoryV2CategoryFilterEntries.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('task.review.inventoryNoCategoriesInLocation')}</p>
              ) : (
                <InventoryCategoryFilterRow
                  options={inventoryV2CategoryFilterEntries}
                  deselectedCategories={inventoryV2DeselectedCategories}
                  onToggle={handleToggleInventoryV2Category}
                  toggleCategoryAriaLabel={(category) => t('task.review.inventoryToggleCategoryAria', { category })}
                  ariaLabel={t('task.review.inventoryFilterCategoriesHeading')}
                />
              )}
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between gap-3 px-1">
                <div className="flex min-w-0 items-center gap-2">
                  <h3 className="text-base font-bold text-gray-900 dark:text-gray-50">
                    {t('task.review.stocktakeResultsHeading')}
                  </h3>
                  {user?.role === USER_ROLE.EMPLOYER && employerInventoryItemTranslations ? (
                    <AutoTranslationHint
                      targetLanguageLabel={employerItemTranslationTargetLabel}
                      showingOriginal={showInventoryItemNamesOriginal}
                      onToggleOriginal={() => setShowInventoryItemNamesOriginal((prev) => !prev)}
                    />
                  ) : null}
                </div>
                <HomeStockLevelLegend />
              </div>
              {inventoryV2VisibleItems.length === 0 && inventoryV2SelectedEmptyCategories.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
                  {t('task.review.inventoryItemsFilteredEmpty')}
                </p>
              ) : (
                <PressableHintProvider>
                <div className="relative z-0 flex flex-col gap-3">
                  {inventoryV2VisibleItems.map((item, itemIndex) => {
                  const stockLevelAriaLabel = inventoryStockLevelUiLabel(item.stockLevel, t);
                  const categoryTrimmed = item.categoryName.trim();
                  const locPhotoUrls = (inventoryV2CurrentLocation?.photoUrls ?? [])
                    .map((u) => (u ?? '').trim())
                    .filter((u) => u !== '');
                  const itemPhotoUrl = (item.photoUrl ?? '').trim();
                  const resolvedItemPhotoUrl = itemPhotoUrl !== '' ? itemPhotoUrl : (locPhotoUrls[0] ?? '');
                  const itemPhotoIndex = Math.max(
                    0,
                    locPhotoUrls.findIndex((u) => u === resolvedItemPhotoUrl),
                  );
                  const displayItemName = resolveInventoryItemNameForDisplay({
                    item,
                    viewerRole: user?.role ?? USER_ROLE.EMPLOYER,
                    viewerLanguage: language,
                    showOriginal:
                      user?.role === USER_ROLE.HELPER
                        ? showTaskContentOriginal
                        : showInventoryItemNamesOriginal,
                  });
                  const displayCategoryLabel =
                    user?.role === USER_ROLE.HELPER
                      ? resolveSubmissionItemCategoryDisplay(
                          categoryTrimmed,
                          item.categoryTranslation,
                          task,
                          showTaskContentOriginal,
                        )
                      : categoryTrimmed;
                  const isLongDisplayItemName =
                    displayItemName.length > INVENTORY_V2_LONG_ITEM_NAME_CHAR_THRESHOLD;
                  return (
                    <div
                      key={`${item.itemName}-${item.categoryName}-${item.sortOrder}-${itemIndex}`}
                      className="relative overflow-visible rounded-2xl border border-gray-200/80 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900"
                    >
                      {categoryTrimmed !== '' ? (
                        <InventoryCategoryBadge
                          category={categoryTrimmed}
                          displayCategory={displayCategoryLabel}
                          badgeIndexMap={inventoryV2TaskCategoryBadgeIndexMap}
                          variant="detail"
                          hintId={`cat-${itemIndex}`}
                        />
                      ) : null}
                      <div className="flex items-stretch gap-3 px-3 py-2.5">
                        {resolvedItemPhotoUrl !== '' && effectiveTaskInstanceId ? (
                          <div className="h-[4.5rem] w-[4.5rem] shrink-0 overflow-hidden rounded-xl border border-gray-200/80 dark:border-gray-600">
                            <ReceiptImageLightbox
                              inventoryV2LocationPhoto={{
                                taskInstanceId: effectiveTaskInstanceId,
                                locationIndex: inventoryV2ResolvedLocationIdx,
                                photoIndex: itemPhotoIndex,
                              }}
                              alt={t('task.review.inventoryItemPhotoAria')}
                              enlargeAriaLabel={t('inventory.enlargePhotoAria')}
                              closeLabel={t('common.closeViewer')}
                              thumbButtonClassName="relative block h-full w-full overflow-hidden rounded-xl text-left focus:outline-none focus:ring-2 focus:ring-primary-500"
                              thumbImgClassName="h-full w-full object-cover"
                            />
                          </div>
                        ) : null}
                        <div className="flex min-h-[4.5rem] min-w-0 flex-1 flex-col justify-center gap-1.5">
                          <p
                            title={displayItemName}
                            className={[
                              'font-bold text-gray-900 dark:text-gray-50',
                              'max-md:line-clamp-none md:line-clamp-3',
                              isLongDisplayItemName
                                ? 'text-sm leading-snug'
                                : 'text-base leading-tight',
                              categoryTrimmed !== '' ? INVENTORY_V2_ITEM_NAME_CATEGORY_BADGE_PR_CLASS : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                          >
                            {displayItemName}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {(item.expiries ?? []).map((e, expIdx) => {
                              if (e.isNoExpiry === true || e.expiryDate === 'NO_EXPIRY') {
                                return (
                                  <span
                                    key={`no-exp-${expIdx}`}
                                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200/80 bg-[#F7F9FC] px-2 py-1 text-[10px] font-medium text-gray-600 dark:border-gray-600 dark:bg-gray-800/80 dark:text-gray-300"
                                  >
                                    <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                      />
                                    </svg>
                                    {t('inventory.expiry.noExpiryLabel')}
                                  </span>
                                );
                              }
                              const d = e.expiryDate.trim();
                              if (!EXPIRY_YMD_REGEX.test(d)) {
                                return null;
                              }
                              const { label: expiryLabel, badgeClassName: expiryBadgeClass } = resolveInventoryV2ExpiryBadge(
                                t,
                                inventoryV2ViewerTodayYmd,
                                d,
                              );
                              const expiryBadgeIcon = (
                                <svg className="h-2.5 w-2.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              );
                              const expiryBadgeBody = (
                                <>
                                  {expiryBadgeIcon}
                                  {expiryLabel}
                                </>
                              );
                              const expiryBadgeClassName = `inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[10px] font-medium ${expiryBadgeClass}`;
                              const showExpiryDateHint = inventoryV2ExpiryBadgeNeedsDateHint(
                                inventoryV2ViewerTodayYmd,
                                d,
                              );
                              if (showExpiryDateHint) {
                                return (
                                  <PressableHint
                                    key={`${d}-${expIdx}`}
                                    hintId={`exp-${itemIndex}-${expIdx}`}
                                    detail={d}
                                    ariaLabel={`${d}, ${expiryLabel}`}
                                    placement="top"
                                    triggerClassName={expiryBadgeClassName}
                                  >
                                    {expiryBadgeBody}
                                  </PressableHint>
                                );
                              }
                              return (
                                <span
                                  key={`${d}-${expIdx}`}
                                  aria-label={`${d}, ${expiryLabel}`}
                                  className={expiryBadgeClassName}
                                >
                                  {expiryBadgeBody}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                        <div
                          className={
                            categoryTrimmed !== ''
                              ? 'mt-4 flex shrink-0 items-center self-start'
                              : 'flex shrink-0 items-center self-center'
                          }
                        >
                          <HomeStockLevelIcon
                            level={item.stockLevel}
                            size="lg"
                            hintId={`jar-${itemIndex}`}
                            tooltipLabel={stockLevelAriaLabel}
                            blinkWhenEmpty
                          />
                        </div>
                      </div>
                    </div>
                  );
                  })}
                  {inventoryV2SelectedEmptyCategories.map((entry) => (
                    <div
                      key={`empty-cat-${entry.category}`}
                      className="rounded-2xl border border-amber-200/90 bg-amber-50/90 p-4 shadow-sm dark:border-amber-800 dark:bg-amber-950/35"
                    >
                      <div className="flex gap-3">
                        <div
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-200/80 text-amber-800 dark:bg-amber-900/70 dark:text-amber-200"
                          aria-hidden
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                            />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[15px] font-bold text-amber-950 dark:text-amber-50">
                            {entry.displayCategory ?? entry.category}
                          </p>
                          <p className="mt-1 text-sm leading-relaxed text-amber-900/90 dark:text-amber-100/90">
                            {t('task.review.inventoryCategoryEmptyAtLocationBody')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </PressableHintProvider>
              )}
            </div>
          </div>
        ) : null}

        {!isInventoryWizardV2Ui && task.taskKind === TASK_KIND.INVENTORY && stocktakePhotoCount > 0 && effectiveTaskInstanceId ? (
          <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/20">
            <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('task.review.stocktakePhotosHeading')}
            </h3>
            <div className="relative">
              <div className="aspect-square overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
                <ReceiptImageLightbox
                  inventoryStocktakePhoto={{
                    taskInstanceId: effectiveTaskInstanceId,
                    photoIndex: stocktakePhotoIndex,
                  }}
                  alt={`${t('task.review.stocktakePhotosHeading')} ${stocktakePhotoIndex + 1}`}
                  enlargeAriaLabel={t('inventory.enlargePhotoAria')}
                  closeLabel={t('common.closeViewer')}
                  thumbButtonClassName="relative block h-full w-full overflow-hidden rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-primary-500"
                  thumbImgClassName="h-full w-full object-contain"
                />
              </div>
              {stocktakePhotoCount > 1 ? (
                <>
                  <button
                    type="button"
                    onClick={handlePrevStocktakePhoto}
                    disabled={stocktakePhotoIndex === 0}
                    className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-opacity hover:bg-black/70 disabled:opacity-30"
                    aria-label={t('task.review.prevPhoto')}
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleNextStocktakePhoto}
                    disabled={stocktakePhotoIndex === stocktakePhotoCount - 1}
                    className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-opacity hover:bg-black/70 disabled:opacity-30"
                    aria-label={t('task.review.nextPhoto')}
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              ) : null}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs text-white">
                {t('task.review.photoCount', { current: stocktakePhotoIndex + 1, total: stocktakePhotoCount })}
              </div>
            </div>
            {stocktakePhotoCount > 1 ? (
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                {Array.from({ length: stocktakePhotoCount }, (_, index) => (
                  <button
                    key={`inv-stock-thumb-${index}`}
                    type="button"
                    onClick={() => setStocktakePhotoIndex(index)}
                    className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 transition-all ${
                      index === stocktakePhotoIndex
                        ? 'border-primary-500'
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                    aria-label={t('task.review.photoCount', { current: index + 1, total: stocktakePhotoCount })}
                    aria-pressed={index === stocktakePhotoIndex}
                  >
                    <TaskInstanceInventoryPhotoImg
                      taskInstanceId={effectiveTaskInstanceId}
                      variant="stocktake"
                      photoIndex={index}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {!isInventoryWizardV2Ui && task.taskKind === TASK_KIND.INVENTORY && inventoryDisplayRows.length > 0 ? (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
              {t('task.review.stocktakeResultsHeading')}
            </h3>
            {inventoryDisplayRows.map((row) => {
              const { line } = row;
              const levelKey =
                line &&
                (line.status === INVENTORY_LEVEL.FULL ||
                  line.status === INVENTORY_LEVEL.HALF ||
                  line.status === INVENTORY_LEVEL.LOW ||
                  line.status === INVENTORY_LEVEL.EMPTY)
                  ? (`inventory.level.${line.status}` as const)
                  : line
                    ? 'inventory.level.unknown'
                    : 'task.review.inventoryLineAwaiting';
              return (
                <div
                  key={row.key}
                  className="rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/20"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-gray-900 dark:text-gray-100">{row.itemName}</p>
                    <span className={getInventoryLevelBadgeClassName(line)}>{t(levelKey)}</span>
                  </div>
                  {!line ? (
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400" role="status">
                      {t('task.review.inventoryLinePendingBody')}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        {!isInventoryWizardV2Ui && task.taskKind === TASK_KIND.INVENTORY ? (
          <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/20">
            <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
              {inventoryLocationSummaryHeading}
            </h3>
            {inventoryReviewSummaryLoading ? (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400" role="status">
                {t('task.review.inventoryLocationSummaryLoading')}
              </p>
            ) : null}
            {!inventoryReviewSummaryLoading &&
            inventoryReviewSummary &&
            inventoryReviewSummary.locations.length > 0 ? (
              <div className="mt-3 space-y-3">
                {inventoryReviewSummary.locations.map((location) => {
                  const submissionLoc = sortedInventoryWizardLocations.find(
                    (l) => (l.locationName ?? '').trim() === location.locationName.trim(),
                  );
                  const displayLocationName =
                    user?.role === USER_ROLE.HELPER && submissionLoc
                      ? resolveSubmissionLocationNameDisplay(submissionLoc, showTaskContentOriginal)
                      : location.locationName;
                  const displayCategoriesWithItems = location.categoriesWithItems.map((cat) =>
                    user?.role === USER_ROLE.HELPER
                      ? resolveHelperInventoryCategoryDisplay({
                          category: cat,
                          loc: submissionLoc,
                          task,
                          items: submissionLoc?.items ?? [],
                          showOriginal: showTaskContentOriginal,
                        })
                      : cat,
                  );
                  const displayCategoriesNoItems = location.categoriesNoItems.map((cat) =>
                    user?.role === USER_ROLE.HELPER && submissionLoc
                      ? resolveSubmissionNoItemsCategoryDisplay(
                          submissionLoc,
                          cat,
                          showTaskContentOriginal,
                        )
                      : cat,
                  );
                  return (
                  <div
                    key={location.locationName}
                    className="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-medium text-gray-900 dark:text-gray-100">{displayLocationName}</p>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {t('task.review.inventoryLocationSummaryCounts', {
                          itemCount: location.itemCount,
                          photoCount: location.photoCount,
                        })}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-1 gap-2 text-xs text-gray-700 dark:text-gray-300 sm:grid-cols-2">
                      <div>
                        <p className="font-medium text-emerald-700 dark:text-emerald-300">
                          {t('task.review.inventoryLocationSummaryWithItems')}
                        </p>
                        <p className="mt-1 break-words">
                          {displayCategoriesWithItems.length > 0
                            ? displayCategoriesWithItems.join(', ')
                            : t('task.review.inventoryLocationSummaryNone')}
                        </p>
                      </div>
                      <div>
                        <p className="font-medium text-amber-700 dark:text-amber-300">
                          {t('task.review.inventoryLocationSummaryNoItems')}
                        </p>
                        <p className="mt-1 break-words">
                          {displayCategoriesNoItems.length > 0
                            ? displayCategoriesNoItems.join(', ')
                            : t('task.review.inventoryLocationSummaryNone')}
                        </p>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : null}
            {!inventoryReviewSummaryLoading &&
            (!inventoryReviewSummary || inventoryReviewSummary.locations.length === 0) &&
            showInventoryWizardPlanPreviewOnReview ? (
              <div
                className="mt-3 space-y-3"
                role="region"
                aria-label={inventoryLocationSummaryHeading}
              >
                {wizardPlanLocationsPreview.length > 0 ? (
                  <div className="flex flex-wrap items-start gap-1.5">
                    <span className="shrink-0 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {t('task.todo.inventoryPreviewLocationsLabel')}
                    </span>
                    <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                      {wizardPlanLocationsPreview.map((loc) => (
                        <span
                          key={`inv-preview-loc:${loc}`}
                          className="inline-flex max-w-full truncate rounded-full border border-teal-200/70 bg-teal-50 px-2 py-0.5 text-[11px] font-semibold text-teal-900 dark:border-teal-800/70 dark:bg-teal-950/40 dark:text-teal-100"
                        >
                          {user?.role === USER_ROLE.HELPER
                            ? resolveTaskWizardLocationDisplay(task, loc, showTaskContentOriginal)
                            : loc}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
                {wizardPlanCategoriesPreview.length > 0 ? (
                  <div className="flex flex-wrap items-start gap-1.5">
                    <span className="shrink-0 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {t('task.todo.inventoryPreviewCategoriesLabel')}
                    </span>
                    <div className="flex min-w-0 flex-1 flex-wrap gap-1">
                      {wizardPlanCategoriesPreview.map((cat) => (
                        <span
                          key={`inv-preview-cat:${cat}`}
                          className="inline-flex max-w-full truncate rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-900 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-100"
                        >
                          {user?.role === USER_ROLE.HELPER
                            ? resolveTaskWizardCategoryDisplay(task, cat, showTaskContentOriginal)
                            : cat}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
            {!inventoryReviewSummaryLoading &&
            (!inventoryReviewSummary || inventoryReviewSummary.locations.length === 0) &&
            !showInventoryWizardPlanPreviewOnReview ? (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {t('task.review.inventoryLocationSummaryEmpty')}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Photo Gallery */}
        {photoCount > 0 && effectiveTaskInstanceId ? (
          <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/20">
            <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('task.review.photos')}
            </h3>

            <div className="relative">
              {/* Main Photo */}
              <div className="aspect-square overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
                <ReceiptImageLightbox
                  taskInstancePhoto={{
                    taskInstanceId: effectiveTaskInstanceId,
                    photoIndex: currentPhotoIndex,
                  }}
                  alt={t('task.complete.photoAlt', { n: currentPhotoIndex + 1 })}
                  enlargeAriaLabel={t('task.complete.enlargePhotoAria')}
                  closeLabel={t('common.closeViewer')}
                  thumbButtonClassName="relative block h-full w-full overflow-hidden rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-primary-500"
                  thumbImgClassName="h-full w-full object-contain"
                />
              </div>

              {/* Navigation Arrows */}
              {photoCount > 1 && (
                <>
                  <button
                    type="button"
                    onClick={handlePrevPhoto}
                    disabled={currentPhotoIndex === 0}
                    className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-opacity hover:bg-black/70 disabled:opacity-30"
                    aria-label={t('task.review.prevPhoto')}
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleNextPhoto}
                    disabled={currentPhotoIndex === photoCount - 1}
                    className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-opacity hover:bg-black/70 disabled:opacity-30"
                    aria-label={t('task.review.nextPhoto')}
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              {/* Photo Counter */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs text-white">
                {t('task.review.photoCount', { current: currentPhotoIndex + 1, total: photoCount })}
              </div>
            </div>

            {/* Thumbnails */}
            {photoCount > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto">
                {Array.from({ length: photoCount }, (_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setCurrentPhotoIndex(index)}
                    className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 transition-all ${
                      index === currentPhotoIndex
                        ? 'border-primary-500'
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <TaskInstancePhotoImg
                      taskInstanceId={effectiveTaskInstanceId}
                      photoIndex={index}
                      alt={`Thumbnail ${index + 1}`}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {/* GPS �?OSM embed preview; external link opens Google Maps */}
        {gpsCoordsValid ? (
          <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/20">
            <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('task.review.gpsLocation')}
            </h3>
            <OsmEmbedInteractiveMap lat={gpsLatNum} lng={gpsLngNum}>
              <div>
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {t('task.review.addressLabel')}
                </h4>
                {gpsAddress ? (
                  <p className="mt-1 text-sm leading-relaxed text-gray-800 dark:text-gray-100">
                    {gpsAddress}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {t('task.review.addressUnavailable')}
                  </p>
                )}
              </div>
            </OsmEmbedInteractiveMap>
          </div>
        ) : null}

        {effectiveTaskInstanceId ? (
          <CommentThread
            targetType={COMMENT_TARGET_TYPE.TASK_INSTANCE}
            targetId={effectiveTaskInstanceId}
            readOnly={isFromPreviousHelper}
          />
        ) : null}

        {/* Error */}
        {error && (
          <div className="rounded-xl bg-red-50 p-4 text-center dark:bg-red-950/40">
            <p className="text-sm text-red-600 dark:text-red-300" role="alert">
              {error}
            </p>
          </div>
        )}
      </main>

      <AppModal
        open={isLatestHomeStockMode && latestStockModalOpen}
        onClose={() => setLatestStockModalOpen(false)}
        title={t('task.review.latestHomeStockRecentTitle')}
        titleId="latest-home-stock-recent-modal-title"
        closeAriaLabel={t('common.close')}
      >
        <ul className="space-y-3">
          {latestStockCandidates.slice(0, 3).map((candidate) => (
            <li key={candidate.taskInstanceId}>
              <button
                type="button"
                onClick={() => handleSelectLatestHomeStockTask(candidate.taskInstanceId)}
                className={`w-full rounded-xl border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                  selectedLatestStockTaskInstanceId === candidate.taskInstanceId
                    ? 'border-primary-400 bg-primary-50 dark:border-primary-600 dark:bg-primary-950/30'
                    : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800'
                }`}
              >
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{candidate.title}</p>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                  {t('task.review.latestHomeStockTaskDate')}: {candidate.dueDate}
                </p>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                  {t('task.review.latestHomeStockSubmittedAt')}: {candidate.submittedAt}
                </p>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-gray-600 dark:text-gray-300">
                    {t('task.review.latestHomeStockStatus')}
                  </span>
                  <StatusBadge status={candidate.status} size="sm" />
                </div>
              </button>
            </li>
          ))}
        </ul>
      </AppModal>

      {canReview ? (
        <EmployerFloatingReviewBar
          onReject={handleOpenNeedsUpdateModal}
          onApprove={() => void handleApproveReview()}
          disabled={reviewing}
          rejectAriaLabel={t('task.review.needsUpdate')}
          approveAriaLabel={t('task.review.approve')}
        />
      ) : null}

      {showHelperLatestHomeStockResubmitBar
        ? portalToBody(
            <div
              className={`${FIXED_ABOVE_BOTTOM_NAV} border-t border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-900`}
              role="region"
              aria-label={t('task.resubmit.submit')}
            >
              <div className="mx-auto max-w-lg">
                <button
                  type="button"
                  onClick={handleHelperStartHomeStockWizard}
                  className="flex w-full min-h-12 items-center justify-center rounded-xl bg-orange-600 py-3 text-base font-bold text-white shadow-md transition-colors hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:bg-orange-600 dark:hover:bg-orange-700"
                  aria-label={t('task.resubmit.submit')}
                >
                  {t('task.resubmit.submit')}
                </button>
              </div>
            </div>,
          )
        : null}

      {showEmployerAddHomeStockFab ? (
        <PagePrimaryFab
          onClick={handleAddHomeStockTaskFabClick}
          ariaLabel={t('task.review.addHomeStockTaskFabAria')}
        >
          <span className="text-lg font-bold leading-none" aria-hidden>+</span>
          <span>{t('task.review.addHomeStockTaskFab')}</span>
        </PagePrimaryFab>
      ) : null}

      <InventorySummaryStatDetailModal
        open={inventoryV2StatModalKind !== null}
        title={
          inventoryV2StatModalKind === 'expiring'
            ? t('inventory.statDetailModal.expiringTitle', {
                days: inventoryV2AggregateStats?.expiringWindowDays ?? 7,
              })
            : t('inventory.statDetailModal.lowStockTitle')
        }
        titleTrailing={
          user?.role === USER_ROLE.EMPLOYER && employerInventoryItemTranslations ? (
            <AutoTranslationHint
              targetLanguageLabel={employerItemTranslationTargetLabel}
              showingOriginal={showInventoryItemNamesOriginal}
              onToggleOriginal={() => setShowInventoryItemNamesOriginal((prev) => !prev)}
            />
          ) : undefined
        }
        categoryLabel={t('inventory.statDetailModal.categoryLabel')}
        locationLabel={t('inventory.statDetailModal.locationLabel')}
        emptyMessage={t('inventory.statDetailModal.empty')}
        expiryHeading={
          inventoryV2StatModalKind === 'expiring' ? t('inventory.statDetailModal.expiryHeading') : undefined
        }
        rows={
          inventoryV2AggregateStats && inventoryV2StatModalKind === 'lowStock'
            ? inventoryV2AggregateStats.lowStockRows.map((r, i) => ({
                rowKey: `employer-low-${i}`,
                itemName: resolveAggregateRowItemName(r),
                categoryName: resolveAggregateRowCategoryName(r),
                locationName: resolveAggregateRowLocationName(r.locationName),
                stockLevel: r.stockLevel,
                stockLevelLabel: inventoryStockLevelUiLabel(r.stockLevel, t),
              }))
            : inventoryV2AggregateStats && inventoryV2StatModalKind === 'expiring'
              ? inventoryV2AggregateStats.expiringRows.map((r, i) => ({
                  rowKey: `employer-exp-${i}`,
                  itemName: resolveAggregateRowItemName(r),
                  categoryName: resolveAggregateRowCategoryName(r),
                  locationName: resolveAggregateRowLocationName(r.locationName),
                  detailLine: r.detailLine,
                }))
              : []
        }
        closeAriaLabel={t('inventory.statDetailModal.closeAria')}
        backdropDismissAriaLabel={t('inventory.statDetailModal.dismissBackdropAria')}
        onClose={handleCloseInventoryV2StatModal}
      />

      <AppModal
        open={needsUpdateModalOpen}
        onClose={handleCloseNeedsUpdateModal}
        title={t('task.review.needsUpdateModalTitle')}
        titleId="task-needs-update-dialog-title"
        describedById="task-needs-update-dialog-desc"
        closeAriaLabel={t('common.cancel')}
        closeDisabled={reviewing}
        hideCloseButton
      >
        <p id="task-needs-update-dialog-desc" className="text-sm text-gray-600 dark:text-gray-300">
          {t('task.review.needsUpdateReasonPrompt')}
        </p>
        <textarea
          value={needsUpdateReasonInput}
          onChange={(event) => setNeedsUpdateReasonInput(event.target.value)}
          rows={4}
          maxLength={500}
          className="mt-3 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          aria-label={t('task.review.needsUpdateReasonPrompt')}
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{needsUpdateReasonInput.length} / 500</p>
        {needsUpdateModalError ? (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
            {needsUpdateModalError}
          </p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <AppButton
            type="button"
            variant="ghost"
            onClick={handleCloseNeedsUpdateModal}
            disabled={reviewing}
          >
            {t('common.cancel')}
          </AppButton>
          <button
            type="button"
            onClick={() => void handleConfirmNeedsUpdateReview()}
            disabled={reviewing}
            className="min-h-11 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 disabled:opacity-50 dark:bg-orange-600 dark:hover:bg-orange-700"
          >
            {reviewing ? t('common.loading') : t('task.review.needsUpdate')}
          </button>
        </div>
      </AppModal>
    </div>
  );
};
